/** @format */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  ActionIcon,
  Badge,
  Modal,
  Table,
  Text,
  Tooltip,
  Group,
} from "@mantine/core";
import { Eye, History, Plus } from "tabler-icons-react";
import { getRelatedAgreement } from "../../utilities/apis/agreements";
import { formatDate } from "../../utilities/utilities";
import { apiWrapWithErrorWithData } from "../../utilities/apiHelpers";
import { getStatusColor, getStatusText } from "../../utilities/enums";
import AgreementAuditLogModal from "./AgreementAuditLogModal";

const RelatedAgreementModal = ({ agreementId }) => {
  const navigate = useNavigate();
  const [relatedAgreementData, setRelatedAgreementData] = useState({
    parent: null,
    children: [],
    loading: true,
  });
  const [dontShowId, setDontShowId] = useState("");
  const [showModalRelated, setShowModalRelated] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [relatedId, setRelatedId] = useState(null);
  const [showAuditId, setShowAuditId] = useState(null);

  const sectionCard = {
    background: "#ffffff",
    border: "1px solid #e9ecef",
    borderRadius: 16,
    padding: 24,
    marginBottom: 24,
    boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
  };

  const sectionHeader = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 18,
    borderBottom: "1px solid #f1f3f5",
    paddingBottom: 12,
  };

  const onViewClick = (agreementviewId) => {
    window.open(
      `/app/agreements_manager/agreements/details/${agreementviewId}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  useEffect(() => {
    const fetchRelatedAgreement = async () => {
      try {
        const response = await apiWrapWithErrorWithData(
          getRelatedAgreement({ agreementId }),
        );
        console.log("Reelated Agreement Response:", response);
        if (response.success) {
          setRelatedAgreementData({
            loading: false,
            parent: response.parent || null,
            children: response.children || [],
          });
        }
      } catch (error) {
        console.error(error);

        setRelatedAgreementData({
          loading: false,
          parent: null,
          children: [],
        });
      }
    };

    fetchRelatedAgreement();
  }, [agreementId]);

  return (
    <>
      <div className="flex flex-col">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: 20,
            marginBottom: 30,
            borderBottom: "1px solid #edf2f7",
          }}
        >
          <div>
            <Text fw={700} size="xl">
              Related Agreements
            </Text>

            <Text c="dimmed" size="sm">
              Agreement #{agreementId}
            </Text>
          </div>

          <Badge size="lg" radius="xl" variant="light" color="blue">
            {(relatedAgreementData.parent ? 1 : 0) +
              relatedAgreementData.children.length}
          </Badge>
        </div>
      </div>
      <Group grow mb={25}>
        <div
          style={{
            border: "1px solid #e9ecef",
            borderRadius: 12,
            padding: 18,
            background: "#f8fafc",
          }}
        >
          <Text size="xs" c="dimmed">
            Parent Agreement
          </Text>

          <Text fw={700}>
            {relatedAgreementData.parent
              ? `Agreement-${relatedAgreementData.parent.id}`
              : "None"}
          </Text>
        </div>

        <div
          style={{
            border: "1px solid #e9ecef",
            borderRadius: 12,
            padding: 18,
            background: "#f8fafc",
          }}
        >
          <Text size="xs" c="dimmed">
            Child Agreements
          </Text>

          <Text fw={700}>{relatedAgreementData.children.length}</Text>
        </div>
      </Group>

      {!relatedAgreementData.loading &&
        !relatedAgreementData.parent &&
        relatedAgreementData.children.length === 0 && (
          <div
            style={{
              padding: 50,
              textAlign: "center",
              border: "1px dashed #ced4da",
              borderRadius: 12,
              background: "#f8f9fa",
            }}
          >
            <Text fw={600} size="lg">
              No Related Agreements Found
            </Text>

            <Text size="sm" c="dimmed" mt={5}>
              Agreement #{agreementId} is not linked to any parent or child
              agreements.
            </Text>
          </div>
        )}

      {!relatedAgreementData?.loading && (
        <div
          className="flex flex-col"
          style={{
            overflowX: "scroll",
            marginTop: "15px",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <div style={sectionCard}>
            <div style={sectionHeader}>
              <Text fw={700} size="md">
                Parent Agreement
              </Text>

              <Badge variant="light" color="blue">
                {relatedAgreementData.parent ? 1 : 0}
              </Badge>
            </div>

            {relatedAgreementData.parent ? (
              <Table
                striped
                highlightOnHover
                style={{
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                <thead>
                  <tr>
                    <th>AIN</th>
                    <th>Type</th>
                    <th>Business Unit</th>
                    <th>Status</th>
                    <th>Second Party</th>
                    <th>Contract Date</th>
                    <th style={{ width: 140 }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td>Agreement-{relatedAgreementData.parent.id}</td>

                    <td>{relatedAgreementData.parent.typeOfAgreement}</td>

                    <td>{relatedAgreementData.parent.mapBusinessunit}</td>

                    <td>
                      <Badge
                        size="lg"
                        radius="xl"
                        color={getStatusColor(
                          relatedAgreementData.parent.status,
                        )}
                      >
                        {getStatusText(relatedAgreementData.parent.status)}
                      </Badge>
                    </td>

                    <td>
                      {relatedAgreementData.parent.secondParty?.name || "-"}
                    </td>

                    <td>
                      {formatDate(relatedAgreementData.parent.dateOfContract)}
                    </td>

                    <td style={{ width: 150 }}>
                      <Group gap="xs" justify="center">
                        <Tooltip label="View Agreement">
                          <ActionIcon
                            variant="subtle"
                            color="white"
                            onClick={() =>
                              onViewClick(relatedAgreementData.parent.id)
                            }
                          >
                            <Eye size={16} />
                          </ActionIcon>
                        </Tooltip>

                        <Tooltip label="Related Agreements">
                          <ActionIcon
                            variant="subtle"
                            color="white"
                            onClick={() => {
                              setRelatedId(relatedAgreementData.parent.id);
                              setShowModalRelated(true);
                            }}
                          >
                            <Plus size={16} />
                          </ActionIcon>
                        </Tooltip>

                        <Tooltip label="Audit Log">
                          <ActionIcon
                            variant="subtle"
                            color="white"
                            onClick={() => {
                              setShowAuditId(relatedAgreementData.parent.id);
                              setShowModal(true);
                            }}
                          >
                            <History size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </td>
                  </tr>
                </tbody>
              </Table>
            ) : (
              <div
                style={{
                  padding: 30,
                  textAlign: "center",
                  background: "#f8fafc",
                  border: "1px dashed #ced4da",
                  borderRadius: 10,
                }}
              >
                <Text c="dimmed" fw={600}>
                  This agreement does not have a parent agreement.
                </Text>
              </div>
            )}
          </div>

          <div style={sectionCard}>
            <div style={sectionHeader}>
              <Text fw={700} size="md">
                Child Agreements
              </Text>

              <Badge variant="light" color="teal">
                {relatedAgreementData.children.length}
              </Badge>
            </div>

            {relatedAgreementData.children.length > 0 ? (
              <Table
                striped
                highlightOnHover
                verticalSpacing="md"
                horizontalSpacing="md"
                style={{
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                <thead>
                  <tr>
                    <th>AIN</th>
                    <th>Type</th>
                    <th>Business Unit</th>
                    <th>Status</th>
                    <th>Second Party</th>
                    <th>Contract Date</th>
                    <th style={{ width: 140 }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {relatedAgreementData.children.map((row) => (
                    <tr key={row.id}>
                      <td>Agreement-{row.id}</td>
                      <td>{row.typeOfAgreement}</td>
                      <td>{row.mapBusinessunit}</td>

                      <td>
                        <Badge color={getStatusColor(row.status)}>
                          {getStatusText(row.status)}
                        </Badge>
                      </td>

                      <td>{row.secondParty?.name || "-"}</td>

                      <td>{formatDate(row.dateOfContract)}</td>

                      <td style={{ width: 150 }}>
                        <Group gap="xs" justify="center">
                          <Tooltip label="View Agreement">
                            <ActionIcon
                              variant="subtle"
                              color="white"
                              onClick={() => onViewClick(row.id)}
                            >
                              <Eye size={16} />
                            </ActionIcon>
                          </Tooltip>

                          <Tooltip label="Related Agreements">
                            <ActionIcon
                              variant="subtle"
                              color="white"
                              onClick={() => {
                                setRelatedId(row.id);
                                setShowModalRelated(true);
                              }}
                            >
                              <Plus size={16} />
                            </ActionIcon>
                          </Tooltip>

                          <Tooltip label="Audit Log">
                            <ActionIcon
                              variant="subtle"
                              color="white"
                              onClick={() => {
                                setShowAuditId(row.id);
                                setShowModal(true);
                              }}
                            >
                              <History size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div
                style={{
                  padding: 30,
                  textAlign: "center",
                  background: "#f8fafc",
                  border: "1px dashed #ced4da",
                  borderRadius: 10,
                }}
              >
                <Text c="dimmed" fw={600}>
                  This agreement does not have any child agreements.
                </Text>
              </div>
            )}
          </div>
        </div>
      )}

      {showModalRelated && (
        <Modal
          overflow="inside"
          opened
          onClose={() => setShowModalRelated(false)} // Call the handler on modal close
          size="calc(80vw)"
        >
          <RelatedAgreementModal agreementId={relatedId} />
        </Modal>
      )}
      {showModal && (
        <Modal
          overflow="inside"
          opened
          onClose={() => setShowModal(false)} // Call the handler on modal close
          size="calc(80vw)"
        >
          <AgreementAuditLogModal agreementId={showAuditId} />
        </Modal>
      )}
    </>
  );
};

export default RelatedAgreementModal;
